import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmpresaContext = createContext(null);

export const useEmpresa = () => {
  const context = useContext(EmpresaContext);
  if (!context) {
    // Retornar objeto vazio com funções noop para evitar erros quando fora do provider
    return {
      empresaAtual: null,
      empresasDisponiveis: [],
      permissoes: [],
      loading: false,
      escritorio: null,
      isUsuarioEscritorio: () => false,
      isAdminEscritorio: () => false,
      temPermissao: () => false,
      trocarEmpresa: async () => false,
      carregarEmpresasDisponiveis: async () => {},
      empresaId: null,
      empresaNome: null,
      totalEmpresas: 0,
    };
  }
  return context;
};

// Perfis de escritório
const PERFIS_ESCRITORIO = [
  'administrador_escritorio',
  'analista_escritorio',
  'auxiliar_escritorio'
];

export const EmpresaProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [empresaAtual, setEmpresaAtual] = useState(null);
  const [empresasDisponiveis, setEmpresasDisponiveis] = useState([]);
  const [permissoes, setPermissoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [escritorio, setEscritorio] = useState(null);

  const api = axios.create({
    baseURL: API_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  api.interceptors.request.use((config) => {
    const storedToken = localStorage.getItem('jornyx_token');
    if (storedToken) {
      config.headers.Authorization = `Bearer ${storedToken}`;
    }
    return config;
  });

  // Verifica se o usuário é de escritório
  const isUsuarioEscritorio = useCallback(() => {
    return user && PERFIS_ESCRITORIO.includes(user.perfil);
  }, [user]);

  // Verifica se é admin do escritório
  const isAdminEscritorio = useCallback(() => {
    return user && user.perfil === 'administrador_escritorio';
  }, [user]);

  // Carrega empresas disponíveis e retorna o array para uso imediato
  const carregarEmpresasDisponiveis = useCallback(async () => {
    if (!token) return [];

    try {
      const response = await api.get('/usuarios/empresas-disponiveis');
      const empresas = response.data || [];
      setEmpresasDisponiveis(empresas);
      
      // Se usuário não é de escritório, a empresa é a única disponível
      if (!isUsuarioEscritorio() && empresas.length === 1) {
        setEmpresaAtual(empresas[0]);
      }
      
      return empresas; // Retorna para uso imediato (evita race condition)
    } catch (error) {
      console.error('Erro ao carregar empresas disponíveis:', error);
      return [];
    }
  }, [token, isUsuarioEscritorio]);

  // Carrega dados do escritório (se aplicável)
  const carregarEscritorio = useCallback(async () => {
    if (!token || !user?.escritorio_id) return;

    try {
      const response = await api.get(`/escritorios/${user.escritorio_id}`);
      setEscritorio(response.data);
    } catch (error) {
      console.error('Erro ao carregar escritório:', error);
    }
  }, [token, user]);

  // Carrega permissões do usuário para a empresa atual
  const carregarPermissoes = useCallback(async () => {
    if (!token || !empresaAtual) return;

    try {
      const response = await api.get('/usuarios/permissoes-empresa');
      setPermissoes(response.data?.permissoes || []);
    } catch (error) {
      console.error('Erro ao carregar permissões:', error);
    }
  }, [token, empresaAtual]);

  // Troca a empresa no contexto (para usuários de escritório)
  const trocarEmpresa = useCallback(async (empresaId) => {
    if (!isUsuarioEscritorio()) {
      console.warn('Apenas usuários de escritório podem trocar de empresa');
      return false;
    }

    try {
      setLoading(true);
      const response = await api.post('/usuarios/contexto', null, {
        params: { empresa_id: empresaId }
      });
      
      // Primeiro tenta usar empresasDisponiveis, senão usa resposta do backend
      let empresaSelecionada = empresasDisponiveis.find(e => e.id === empresaId);
      
      // Se não encontrou localmente, usar resposta do backend ou buscar da API
      if (!empresaSelecionada) {
        if (response.data?.empresa) {
          empresaSelecionada = response.data.empresa;
        } else {
          // Fallback: buscar empresa diretamente
          try {
            const empresaResponse = await api.get(`/empresas/${empresaId}`);
            empresaSelecionada = empresaResponse.data;
          } catch (e) {
            console.error('Erro ao buscar empresa:', e);
          }
        }
      }
      
      setEmpresaAtual(empresaSelecionada);
      
      // Salvar no localStorage para persistência
      localStorage.setItem('jornyx_empresa_atual', empresaId);
      
      // Recarregar permissões
      await carregarPermissoes();
      
      return true;
    } catch (error) {
      console.error('Erro ao trocar empresa:', error);
      return false;
    } finally {
      setLoading(false);
    }
  }, [isUsuarioEscritorio, empresasDisponiveis, carregarPermissoes]);

  // Verifica se o usuário tem uma permissão específica
  const temPermissao = useCallback((permissao) => {
    // Admin plataforma tem todas as permissões
    if (user?.perfil === 'administrador_plataforma') return true;
    
    // Admin escritório tem todas as permissões
    if (isAdminEscritorio()) return true;
    
    // Admin empresa tem todas as permissões
    if (user?.perfil === 'administrador_empresa') return true;
    
    return permissoes.includes(permissao);
  }, [user, isAdminEscritorio, permissoes]);

  // Inicialização
  useEffect(() => {
    const inicializar = async () => {
      if (!token || !user) {
        setLoading(false);
        return;
      }

      setLoading(true);
      
      try {
        // Carregar empresas disponíveis e usar resultado direto (evita race condition)
        const empresasCarregadas = await carregarEmpresasDisponiveis();
        
        // Se é usuário de escritório, carregar dados do escritório
        if (isUsuarioEscritorio()) {
          await carregarEscritorio();
          
          // Tentar restaurar empresa do localStorage usando o resultado direto
          const empresaSalva = localStorage.getItem('jornyx_empresa_atual');
          if (empresaSalva && empresasCarregadas.length > 0) {
            const empresa = empresasCarregadas.find(e => e.id === empresaSalva);
            if (empresa) {
              setEmpresaAtual(empresa);
            }
          }
        } else if (user.empresa_id) {
          // Usuário de empresa - carregar empresa diretamente
          try {
            const response = await api.get(`/empresas/${user.empresa_id}`);
            setEmpresaAtual(response.data);
          } catch (error) {
            console.error('Erro ao carregar empresa do usuário:', error);
          }
        }
      } catch (error) {
        console.error('Erro na inicialização do contexto de empresa:', error);
      } finally {
        setLoading(false);
      }
    };

    inicializar();
  }, [token, user]);

  // Carregar permissões quando empresa atual mudar
  useEffect(() => {
    if (empresaAtual) {
      carregarPermissoes();
    }
  }, [empresaAtual, carregarPermissoes]);

  const value = {
    // Estado
    empresaAtual,
    empresasDisponiveis,
    permissoes,
    loading,
    escritorio,
    
    // Funções de verificação
    isUsuarioEscritorio,
    isAdminEscritorio,
    temPermissao,
    
    // Ações
    trocarEmpresa,
    carregarEmpresasDisponiveis,
    
    // Helpers
    empresaId: empresaAtual?.id,
    empresaNome: empresaAtual?.nome_fantasia || empresaAtual?.razao_social,
    totalEmpresas: empresasDisponiveis.length,
  };

  return (
    <EmpresaContext.Provider value={value}>
      {children}
    </EmpresaContext.Provider>
  );
};

export default EmpresaContext;
